<template>
	<div>
		<keep-alive>
			<component
				:is="performanceReturnComponent.component"
				v-bind="performanceReturnComponent.props"
				@return-chip-selected="setSelectedReturnJumboChip"
			/>
		</keep-alive>

		<div :style="`height: ${chartHeight}px`">
			<BarChart
				class="vcx-mt-200"
				:chart-series="chartSeries"
				:custom-options="customOptions"
				value-format="currency"
				:y-axis-bounds="yAxisBounds"
				:reflow-chart="reflowChart"
				behavior="select-all"
				data-test="desktop-net-returns-chart"
				@mouse-over="onNetReturnsMouseOver"
				@mouse-out="onNetReturnsMouseOut"
			/>
		</div>

		<div class="vcx-ml-600-lg vcx-mt-100">
			<VcxChips
				:chips="timePeriodChips"
				:selected="timeSelection"
				@update:model-value="changeReturnsBreakdownView"
			></VcxChips>
		</div>
	</div>
</template>

<script setup lang="ts">
import { BarChartDataSeries, BarChartDataSeriesPoint } from 'types/charts/bar-chart';
import { computed, onActivated, onDeactivated, ref } from 'vue';
import { getFormattedReturnsData, isCurrentTimePeriod } from '@utils/performance-portfolio';
import { HypotheticalInvestmentReturns, HypotheticalInvestmentReturnsByTimePeriod } from 'types/funds';
import {
	PerformanceReturnJumboChip,
	PerformanceReturnJumboChipEnum,
	Return,
	TimePeriodChip
} from 'types/performance-portfolio';
import BarChart from '@charts/bar-chart.vue';
import { Chip } from 'types/layout';
import { DynamicComponent } from 'types/vue';
import { Options } from 'highcharts';
import PortfolioPerformanceReturnRadio from '@components/account/portfolio/portfolio-performance-return-radio.vue';
import { roundUpToMultipleOfTens } from '@utils/number';
import { useAppStore } from '@stores/app';
import VcxChips from '@components/vcx/vcx-chips-component.vue';
import VcxPortfolioPerformanceReturnJumboChips from '@components/vcx/vcx-portfolio-performance-return-jumbo-chips.vue';
import { YAxisBounds } from 'types/charts';

interface Props {
	returnsByTimePeriod: HypotheticalInvestmentReturnsByTimePeriod;
	totalInvestmentValue?: string | undefined;
	isAnnualized?: boolean;
	showDistributionFootnote: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isAnnualized: false,
	totalInvestmentValue: undefined
});

const appStore = useAppStore();

const performanceJumboChips: Array<PerformanceReturnJumboChip> = [
	{
		name: 'Distributions',
		enum: 'DISTRIBUTIONS',
		indicatorClass: '',
		return: ''
	},
	{
		name: 'Appreciation',
		enum: 'APPRECIATION',
		indicatorClass: '',
		return: ''
	},
	{
		name: 'Net return',
		enum: 'NET_RETURN',
		indicatorClass: '',
		return: ''
	}
];
const timePeriodChips: Array<TimePeriodChip> = [
	{ value: 'Quarterly', label: 'Quarterly' },
	{ value: 'Annually', label: 'Annually' }
];

const hoveredQuarter = ref<HypotheticalInvestmentReturns | null>(null);
const hoveredYear = ref<HypotheticalInvestmentReturns | null>(null);
const reflowChart = ref(false);
const selectedPerformanceReturnChip = ref<PerformanceReturnJumboChipEnum>('NET_RETURN');
const timeSelection = ref<TimePeriodChip>(timePeriodChips[0]);

const activeTimePeriod = computed(
	(): HypotheticalInvestmentReturns | null => hoveredQuarter.value ?? hoveredYear.value ?? null
);

const chartData = computed((): Array<BarChartDataSeriesPoint> => {
	if (timeSelection.value.value === 'Quarterly') {
		const hasMultipleYears = props.returnsByTimePeriod.quarterlyReturnDetails?.some(
			(q) => q.year !== props.returnsByTimePeriod.quarterlyReturnDetails?.[0]?.year
		);

		const temp = (props.returnsByTimePeriod.quarterlyReturnDetails ?? []).reduce(
			(acc, quarter) => {
				if (quarter.year) {
					let label = `Q${quarter.quarter} '${String(quarter.year).substring(2, 4)}`;

					if (hasMultipleYears) {
						label = !acc.years[quarter.year] ? label : '';
						acc.years[quarter.year] = true; // Safely use quarter.year as index
					}

					acc.data.push({
						key: `Q${quarter.quarter} ${quarter.year}`,
						label,
						value: chartValue(quarter),
						...(isCurrentTimePeriod({
							timeSelection: timeSelection.value,
							year: quarter.year,
							quarter: quarter.quarter
						}) && { classes: ['reduced-opacity'] })
					});
				}

				return acc;
			},
			{ data: [] as Array<BarChartDataSeriesPoint>, years: {} as Record<string, boolean> }
		);

		return temp.data;
	} else {
		return (props.returnsByTimePeriod.yearlyReturnDetails ?? []).map((year: HypotheticalInvestmentReturns) => ({
			key: `${year.year}`,
			label: `${year.year}`,
			value: chartValue(year),
			...(isCurrentTimePeriod({
				timeSelection: timeSelection.value,
				year: Number(year.year)
			}) && { classes: ['reduced-opacity'] })
		}));
	}
});

const chartHeight = computed((): number => (appStore.isMobile ? 250 : 350));

const chartSeries = computed((): BarChartDataSeries => {
	return {
		data: chartData.value,
		color: '#335565',
		activeColor: '#4C7E96'
	};
});

const customOptions = computed((): Options => {
	return {
		chart: {
			height: chartHeight.value,
			marginBottom: 50,
			backgroundColor: 'transparent'
		},
		tooltip: {
			backgroundColor: 'transparent'
		},
		xAxis: {
			labels: {
				style: {
					color: '#9d9c98'
				}
			}
		},
		yAxis: {
			gridLineColor: '#6b6a65',
			gridLineDashStyle: 'Dash',
			labels: {
				style: {
					color: '#9d9c98'
				}
			}
		}
	};
});

const displayAnnualizedLabel = computed((): boolean => props.isAnnualized && timeSelection.value.value === 'Annually');

const performanceReturnComponent = computed((): DynamicComponent => {
	return appStore.isMobile
		? {
				component: PortfolioPerformanceReturnRadio,
				props: {
					data: netReturnOverviewData.value,
					selectedChip: selectedPerformanceReturnChip.value,
					showReturnIndicator: false
				}
			}
		: {
				component: VcxPortfolioPerformanceReturnJumboChips,
				props: {
					data: netReturnOverviewData.value,
					selectedChip: selectedPerformanceReturnChip.value,
					showReturnIndicator: false
				}
			};
});

const netReturnOverviewData = computed(
	(): Array<PerformanceReturnJumboChip> =>
		appStore.isMobile ? formattedPerformanceRadios.value : formattedPerformanceReturnData.value
);

const formattedPerformanceRadios = computed((): Array<PerformanceReturnJumboChip> => {
	return performanceRadios.value.map((x, idx) => {
		return {
			name: x.name,
			...getFormattedReturnsData(performanceReturnData.value[idx], '$', false, true),
			enum: x.enum,
			footnote: x.name === 'Distributions' && props.showDistributionFootnote ? '1' : undefined
		};
	});
});

const formattedPerformanceReturnData = computed((): Array<PerformanceReturnJumboChip> => {
	return performanceJumboChips.map((x, idx) => {
		return {
			name: x.name,
			...getFormattedReturnsData(performanceReturnData.value[idx], '$', false, true),
			enum: x.enum,
			footnote: x.name === 'Distributions' && props.showDistributionFootnote ? '1' : undefined
		};
	});
});

const performanceRadios = computed((): Array<PerformanceReturnJumboChip> => {
	return [
		{
			name: 'Distributions',
			enum: 'DISTRIBUTIONS',
			indicatorClass: '',
			return: ''
		},
		{
			name: 'Appreciation',
			enum: 'APPRECIATION',
			indicatorClass: '',
			return: ''
		},
		{
			name: displayAnnualizedLabel.value ? 'Net annualized return' : 'Net return',
			enum: 'NET_RETURN',
			indicatorClass: '',
			return: ''
		}
	];
});

const performanceReturnData = computed((): Array<Return> => {
	return [
		{
			amount: netDividends.value ?? '',
			percentage: ''
		},
		{
			amount: netAppreciation.value ?? '',
			percentage: ''
		},
		{
			amount: netReturns.value ?? '',
			percentage: ''
		}
	];
});

const netAppreciation = computed((): string => {
	if (activeTimePeriod.value) {
		return activeTimePeriod.value.netAppreciation;
	} else {
		return props.returnsByTimePeriod?.allTimeReturnDetails?.netAppreciation ?? '';
	}
});

const netDividends = computed((): string => {
	if (activeTimePeriod.value) {
		return activeTimePeriod.value.netDividends;
	} else {
		return props.returnsByTimePeriod?.allTimeReturnDetails?.netDividends ?? '';
	}
});

const netReturns = computed((): string => {
	if (activeTimePeriod.value) {
		return activeTimePeriod.value.totalReturns;
	} else {
		return props.returnsByTimePeriod?.allTimeReturnDetails?.totalReturns ?? '';
	}
});

const yAxisBounds = computed((): YAxisBounds | undefined => {
	const selectedNetReturnsData =
		timeSelection.value.value === 'Quarterly'
			? props.returnsByTimePeriod.quarterlyReturnDetails
			: props.returnsByTimePeriod.yearlyReturnDetails;

	if (selectedNetReturnsData) {
		if (selectedNetReturnsData.length === 0) {
			const totalAccountMax = roundUpToMultipleOfTens(Number(props.totalInvestmentValue ?? '0') * 0.1);
			const noDataMax = totalAccountMax > 2 ? totalAccountMax : 10;

			return {
				yAxisMin: 0,
				yAxisMax: noDataMax
			};
		} else {
			return getMaxMinReturns(selectedNetReturnsData);
		}
	}

	return undefined;
});

onActivated(() => (reflowChart.value = true));

onDeactivated(() => (reflowChart.value = false));

function changeReturnsBreakdownView(selection: TimePeriodChip | Chip | undefined): void {
	timeSelection.value = selection as TimePeriodChip;
}

function chartValue(timePeriod: HypotheticalInvestmentReturns): number {
	switch (selectedPerformanceReturnChip.value) {
		case 'APPRECIATION':
			return Number(timePeriod.netAppreciation);
		case 'DISTRIBUTIONS':
			return Number(timePeriod.netDividends);
		case 'NET_RETURN':
		default:
			return Number(timePeriod.totalReturns);
	}
}

function getMaxMinReturns(returnsData: Array<HypotheticalInvestmentReturns>): YAxisBounds {
	const allMaxReturns = returnsData.map((data) =>
		Math.max(Number(data.netAppreciation), Number(data.netDividends), Number(data.totalReturns))
	);

	const allMinReturns = returnsData.map((data) =>
		Math.min(Number(data.netAppreciation), Number(data.netDividends), Number(data.totalReturns))
	);

	const maxReturn = allMaxReturns.reduce((a, b) => Math.max(a, b));
	const minReturn = allMinReturns.reduce((a, b) => Math.min(a, b));

	return {
		yAxisMax: maxReturn * 1.2,
		yAxisMin: minReturn > 0 ? 0 : minReturn
	};
}

function onNetReturnsMouseOver(id: string): void {
	if (timeSelection.value.value === 'Quarterly') {
		const quarter = (props.returnsByTimePeriod?.quarterlyReturnDetails ?? []).find(
			(qtr) => `Q${qtr.quarter} ${qtr.year}` === id
		);
		hoveredQuarter.value = quarter ?? null;
	} else if (timeSelection.value.value === 'Annually') {
		const year = (props.returnsByTimePeriod?.yearlyReturnDetails ?? []).find((yr) => String(yr.year) === id);
		hoveredYear.value = year ?? null;
	}
}

function onNetReturnsMouseOut(): void {
	hoveredQuarter.value = null;
	hoveredYear.value = null;
}

function setSelectedReturnJumboChip(chip: PerformanceReturnJumboChipEnum) {
	selectedPerformanceReturnChip.value = chip;
}
</script>

<style lang="scss" scoped>
.performance-return-jumbo-chips-wrapper {
	margin-left: 64px;
	margin-top: 0;
}

:deep(.highcharts-tooltip > span) {
	background: #cedde3;
	border-radius: 35px;
	padding: 6px 8px;
}
</style>
